/* =========================================================================
   acete — theme core

   Same behaviour as the static build, with one substitution: the localStorage
   cart is replaced by Shopify's AJAX Cart API. The drawer, the reveal plumbing
   and the scroll choreography are untouched, because they only ever worked off
   whatever list of lines they were handed.

   Owns: momentum scrolling, PRESSURE (the value the weave runs on), reveals,
   nav, accordions, the cart, and the scroll choreography.
   ========================================================================= */


const root = document.documentElement;
const REDUCED = matchMedia("(prefers-reduced-motion: reduce)").matches;
const hasGSAP = typeof window.gsap !== "undefined";
const ROUTES = (window.acete && window.acete.routes) || {
  cart: "/cart", cartAdd: "/cart/add", cartChange: "/cart/change",
};

/* ------------------------------------------------------------ PRESSURE ---
   Scroll velocity, smoothed, fast attack and a ~0.9s release. Nothing here is
   driven by scroll POSITION, which is what keeps the weave from being an
   ordinary parallax with a new name.                                        */
export const Pressure = {
  value: 0,
  _raw: 0,
  _feed(v) { this._raw = Math.abs(v); },
  _tick() {
    const target = Math.min(this._raw / 42, 1);
    const k = target > this.value ? 0.20 : 0.055;
    this.value += (target - this.value) * k;
    if (this.value < 0.0008) this.value = 0;
    root.style.setProperty("--pressure", this.value.toFixed(4));
    this._raw *= 0.86;
  },
};

/* --------------------------------------------------------------- money --- */
export function money(cents) {
  const fmt = (window.acete && window.acete.moneyFormat) || "${{amount}}";
  const n = (cents || 0) / 100;
  const withCommas = (v, dec) =>
    v.toFixed(dec).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fmt
    .replace(/\{\{\s*amount\s*\}\}/g, withCommas(n, 2))
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withCommas(n, 0))
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g,
             n.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, "."))
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
             n.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, "."));
}

/* -------------------------------------------------------------- scroll --- */
let lenis = null;

function initScroll() {
  if (REDUCED || typeof window.Lenis === "undefined") {
    let last = window.scrollY;
    addEventListener("scroll", () => {
      const y = window.scrollY;
      Pressure._feed((y - last) * 0.9);
      last = y;
    }, { passive: true });
    const loop = () => { Pressure._tick(); requestAnimationFrame(loop); };
    requestAnimationFrame(loop);
    return;
  }
  lenis = new window.Lenis({
    duration: 1.15,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true, touchMultiplier: 1.6, lerp: 0.09,
  });
  lenis.on("scroll", (e) => {
    Pressure._feed(e.velocity);
    if (hasGSAP && window.ScrollTrigger) window.ScrollTrigger.update();
  });
  if (hasGSAP) {
    window.gsap.ticker.add((t) => { lenis.raf(t * 1000); Pressure._tick(); });
    window.gsap.ticker.lagSmoothing(0);
  } else {
    const raf = (t) => { lenis.raf(t); Pressure._tick(); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
  }
}

export const scrollTo = (t) => {
  if (lenis) lenis.scrollTo(t, { offset: -20 });
  else document.querySelector(t)?.scrollIntoView({ behavior: "smooth" });
};

/* --------------------------------------------------------- line chooser -- */
/* The catalog item in the bar opens a chooser instead of leading to the mix.
   CSS opens it on hover where there is a pointer; this handles the rest —
   click to open without one, Escape and outside-click to close, and the
   aria-expanded state a screen reader needs either way. */
function initNavMenus() {
  const items = [...document.querySelectorAll("[data-navsub]")];
  if (!items.length) return;
  const fine = matchMedia("(hover: hover) and (pointer: fine)").matches;

  const close = (li) => {
    li.classList.remove("is-open");
    li.querySelector("a")?.setAttribute("aria-expanded", "false");
  };
  const closeAll = () => items.forEach(close);

  items.forEach((li) => {
    const link = li.querySelector("a");
    if (!link) return;

    link.addEventListener("click", (e) => {
      // With a pointer the panel is already open on hover, so the link does
      // what a link does. Without one, the first tap opens it and the second
      // follows — otherwise the chooser could never be seen on a phone.
      if (fine) return;
      if (!li.classList.contains("is-open")) {
        e.preventDefault();
        closeAll();
        li.classList.add("is-open");
        link.setAttribute("aria-expanded", "true");
      }
    });

    if (fine) {
      li.addEventListener("pointerenter", () => link.setAttribute("aria-expanded", "true"));
      li.addEventListener("pointerleave", () => close(li));
    }
    li.addEventListener("focusin", () => {
      li.classList.add("is-open");
      link.setAttribute("aria-expanded", "true");
    });
    li.addEventListener("focusout", (e) => {
      if (!li.contains(e.relatedTarget)) close(li);
    });
  });

  document.addEventListener("keydown", (e) => { if (e.key === "Escape") closeAll(); });
  document.addEventListener("click", (e) => {
    if (!e.target.closest("[data-navsub]")) closeAll();
  });
}

/* ------------------------------------------------------- shop furniture -- */
/* Two small things the shop pages need and nothing else does: the filter bar
   gets out of the way on the way down, and there is one way back to the top. */
function initShopScroll() {
  const bar = document.querySelector(".shopbar");
  const top = document.querySelector("[data-totop]");
  if (!bar && !top) return;

  let last = window.scrollY;
  let ticking = false;

  function frame() {
    const y = window.scrollY;
    const down = y > last;
    const past = y > 260;

    if (bar) bar.classList.toggle("is-gone", down && past);
    if (top) top.classList.toggle("is-on", y > window.innerHeight);

    last = y;
    ticking = false;
  }

  addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(frame);
  }, { passive: true });

  // Glide, not a jump. Reduced motion still gets there instantly, because an
  // animated 4000px scroll is exactly the kind of movement that setting is for.
  top?.addEventListener("click", () => {
    const instant = matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (lenis) lenis.scrollTo(0, instant ? { immediate: true } : { duration: 1.1 });
    else window.scrollTo({ top: 0, behavior: instant ? "auto" : "smooth" });
  });
}

/* ------------------------------------------------------------- reveals --- */
function initReveals(scope = document) {
  const els = scope.querySelectorAll("[data-rise]:not(.is-in), [data-wipe]:not(.is-in)");
  if (!els.length) return;
  if (!("IntersectionObserver" in window)) {
    els.forEach((el) => el.classList.add("is-in"));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add("is-in");
      io.unobserve(e.target);
    });
    // threshold stays 0: a plate can be taller than the viewport, and a
    // ratio-based threshold is unreachable once it is
  }, { rootMargin: "0px 0px -8% 0px", threshold: 0 });

  els.forEach((el) => {
    const group = el.closest("[data-stagger]");
    if (group) {
      const sibs = [...group.querySelectorAll("[data-rise], [data-wipe]")];
      el.style.setProperty("--d", `${Math.min(sibs.indexOf(el), 6) * 70}ms`);
    }
    io.observe(el);
  });
}

/* ----------------------------------------------------------------- nav --- */
function initNav() {
  const nav = document.querySelector("[data-nav]");
  if (!nav) return;

  if (nav.hasAttribute("data-nav-static")) {
    nav.classList.add("is-open");
  } else {
    let last = window.scrollY;
    addEventListener("scroll", () => {
      const y = window.scrollY;
      if (y < 80 || y > last) nav.classList.remove("is-open");
      else if (last - y > 6) nav.classList.add("is-open");
      last = y;
    }, { passive: true });
  }

  nav.querySelectorAll('a[href^="#"]').forEach((a) => {
    a.addEventListener("click", (e) => {
      const id = a.getAttribute("href");
      if (id.length < 2 || !document.querySelector(id)) return;
      e.preventDefault();
      scrollTo(id);
      nav.classList.remove("is-open");
    });
  });
}

/* ---------------------------------------------------------- accordions --- */
function initAccordions(scope = document) {
  scope.querySelectorAll("[data-acc] .acc__btn").forEach((btn) => {
    if (btn.dataset.bound) return;
    btn.dataset.bound = "1";
    const body = btn.nextElementSibling;
    if (!body) return;
    body.hidden = btn.getAttribute("aria-expanded") !== "true";
    btn.addEventListener("click", () => {
      const open = btn.getAttribute("aria-expanded") === "true";
      btn.setAttribute("aria-expanded", String(!open));
      body.hidden = open;
    });
  });
}

/* ---------------------------------------------------------------- cart --- */
const listeners = new Set();
let state = { items: [], total: 0, count: 0 };

const normalise = (cart) => ({
  items: (cart.items || []).map((i) => ({
    key: i.key,                       // a LINE key, not a variant id
    title: i.product_title,
    variant: i.variant_title && i.variant_title !== "Default Title" ? i.variant_title : "",
    price: i.final_line_price,
    unit: i.final_price,
    image: i.image,
    url: i.url,
    qty: i.quantity,
  })),
  total: cart.total_price || 0,
  count: cart.item_count || 0,
});

const publish = () => listeners.forEach((fn) => fn(state));

async function api(url, body) {
  const res = await fetch(url + ".js", {
    method: body ? "POST" : "GET",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.description || data.message || "Cart error");
  return data;
}

export const Cart = {
  get state() { return state; },
  subscribe(fn) { listeners.add(fn); fn(state); return () => listeners.delete(fn); },
  async refresh() { state = normalise(await api(ROUTES.cart)); publish(); return state; },
  async add(id, quantity = 1, properties) {
    await api(ROUTES.cartAdd, { items: [{ id, quantity, properties }] });
    return this.refresh();          // every write returns the whole cart; re-read
  },                                 // rather than mutate, or the drawer drifts
  async setQty(key, quantity) {
    await api(ROUTES.cartChange, { id: key, quantity });
    return this.refresh();
  },
  remove(key) { return this.setQty(key, 0); },
};

/* --------------------------------------------------------------- drawer -- */
function initDrawer() {
  const drawer = document.querySelector("[data-drawer]");
  if (!drawer) return;
  const panel = drawer.querySelector(".drawer__panel");
  const body = drawer.querySelector("[data-cart-items]");
  const totalEl = drawer.querySelector("[data-cart-total]");
  const checkout = drawer.querySelector("[data-checkout]");
  let lastFocus = null;

  const open = () => {
    lastFocus = document.activeElement;
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add("is-open"));
    lenis?.stop();
    drawer.querySelector(".drawer__close")?.focus();
  };
  const close = () => {
    drawer.classList.remove("is-open");
    lenis?.start();
    setTimeout(() => { drawer.hidden = true; lastFocus?.focus(); }, 420);
  };
  window.aceteOpenCart = open;

  document.querySelectorAll("[data-cart-open]").forEach((b) =>
    b.addEventListener("click", (e) => { e.preventDefault(); open(); }));
  drawer.querySelector(".drawer__scrim")?.addEventListener("click", close);
  drawer.querySelector(".drawer__close")?.addEventListener("click", close);

  addEventListener("keydown", (e) => {
    if (drawer.hidden) return;
    if (e.key === "Escape") return close();
    if (e.key !== "Tab") return;
    const f = panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  Cart.subscribe((s) => {
    document.querySelectorAll("[data-cart-count]").forEach((el) => {
      el.textContent = String(s.count);
    });
    if (!body) return;
    body.innerHTML = s.items.length ? s.items.map((i) => `
      <div class="citem">
        <img src="${i.image ? i.image.replace(/(\.[a-z]+)(\?|$)/i, "_160x200_crop_center$1$2") : ""}" alt="" width="72" height="90" loading="lazy">
        <div>
          <div class="citem__top">
            <a class="citem__name" href="${i.url}">${i.title}</a>
            <span class="tnum">${money(i.price)}</span>
          </div>
          ${i.variant ? `<div class="citem__meta">${i.variant}</div>` : ""}
          <div class="citem__qty">
            <button type="button" data-dec="${i.key}" aria-label="Decrease quantity of ${i.title}">&minus;</button>
            <span class="citem__n tnum">${i.qty}</span>
            <button type="button" data-inc="${i.key}" aria-label="Increase quantity of ${i.title}">+</button>
            <button type="button" class="citem__rm" data-rm="${i.key}">Remove</button>
          </div>
        </div>
      </div>`).join("") : '<p class="drawer__empty">Nothing here yet.</p>';
    if (totalEl) totalEl.textContent = money(s.total);
    if (checkout) checkout.disabled = s.items.length === 0;
  });

  body?.addEventListener("click", async (e) => {
    const t = e.target.closest("button[data-inc], button[data-dec], button[data-rm]");
    if (!t) return;
    const line = (k) => s.items.find((i) => i.key === k);
    const s = Cart.state;
    t.disabled = true;
    try {
      if (t.dataset.inc) await Cart.setQty(t.dataset.inc, line(t.dataset.inc).qty + 1);
      if (t.dataset.dec) await Cart.setQty(t.dataset.dec, line(t.dataset.dec).qty - 1);
      if (t.dataset.rm) await Cart.remove(t.dataset.rm);
    } catch (err) {
      console.error(err);
      t.disabled = false;
    }
  });
}

/* ------------------------------------------------- scroll choreography --- */
function initChoreography() {
  if (!hasGSAP || !window.ScrollTrigger || REDUCED) return;
  const { gsap } = window;
  gsap.registerPlugin(window.ScrollTrigger);

  const hero = document.querySelector("[data-hero]");
  if (hero) {
    gsap.timeline({ scrollTrigger: { trigger: hero, start: "top top", end: "bottom top", scrub: 0.8 } })
      .to(hero.querySelector(".hero__bg"), { yPercent: 13, scale: 1.06, ease: "none" }, 0)
      .to(hero.querySelector(".hero__fg"), { yPercent: 26, ease: "none" }, 0)
      .to(hero.querySelector(".hero__inner"), { yPercent: -9, ease: "none" }, 0)
      .to(hero.querySelector(".hero__mark"), { yPercent: -22, opacity: 0.16, ease: "none" }, 0);
  }

  const man = document.querySelector("[data-manifesto]");
  if (man) {
    const lines = man.querySelectorAll(".mline");
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: man, start: "top top",
        end: () => "+=" + window.innerHeight * 3.2,
        pin: man.querySelector(".manifesto__pin"), scrub: 0.9, anticipatePin: 1,
      },
    });
    gsap.set(lines, { opacity: 0, yPercent: 40 });
    lines.forEach((line, i) => {
      tl.to(line, { opacity: 1, yPercent: 0, ease: "power2.out", duration: 1 }, i * 0.85)
        .to(line, { opacity: i === lines.length - 1 ? 1 : 0.18, duration: 0.6 }, i * 0.85 + 1.1);
    });
  }

  gsap.utils.toArray(".objects .object__frame").forEach((frame, i) => {
    gsap.fromTo(frame, { yPercent: 4 + (i % 2) * 3 },
      { yPercent: -(4 + (i % 2) * 3), ease: "none",
        scrollTrigger: { trigger: frame, start: "top bottom", end: "bottom top", scrub: 1 } });
  });

  const drop = document.querySelector("[data-drop]");
  if (drop) {
    gsap.fromTo(drop.querySelector(".drop__bg"), { yPercent: -6 },
      { yPercent: 6, ease: "none",
        scrollTrigger: { trigger: drop, start: "top bottom", end: "bottom top", scrub: 1 } });
  }

  /* GENERIC DEPTH — anything carrying data-depth drifts by that many percent
     against the scroll. Used by the about spread so its three plates move at
     three rates: equal movement is one image cut into pieces, and the eye
     reads the difference, not the motion. */
  gsap.utils.toArray("[data-depth]").forEach((el) => {
    const d = parseFloat(el.dataset.depth) || 0;
    if (!d) return;
    gsap.fromTo(el,
      { yPercent: d }, { yPercent: -d, ease: "none",
        scrollTrigger: { trigger: el, start: "top bottom", end: "bottom top", scrub: 1 } });
  });

  const mark = document.querySelector("[data-colophon-mark]");
  if (mark) {
    gsap.fromTo(mark, { clipPath: "inset(0 0 78% 0)", opacity: 0.25 },
      { clipPath: "inset(0 0 0% 0)", opacity: 0.92, ease: "power2.out",
        scrollTrigger: { trigger: mark, start: "top 92%", end: "top 34%", scrub: 0.7 } });
  }
}

/* --------------------------------------------------------- plate index --- */
function initPlateIndex() {
  /* A numeral whose section is not on this page points at nothing, so it is
     removed rather than offered. Sections come and go from the running order —
     the statement stepped aside for the shop preview — and the index has to
     survive that without leaving a dead rung in the ladder. */
  const links = [...document.querySelectorAll("[data-plate]")].filter((a) => {
    const t = document.querySelector(a.getAttribute("href"));
    if (t) return true;
    a.remove();
    return false;
  });
  if (!links.length) return;
  const targets = links.map((a) => document.querySelector(a.getAttribute("href")));
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const i = targets.indexOf(e.target);
      links.forEach((a, n) => a.setAttribute("aria-current", String(n === i)));
    });
  }, { rootMargin: "-45% 0px -45% 0px" });
  targets.forEach((t) => t && io.observe(t));
}

/* ------------------------------------------------------------ enhance --- */
/* The weave drags in Three.js, 650KB of it, and the hero sequence is a
   nicety. Neither is worth taking the page down for, so they load on their own
   and failure is caught. A static import here would put the cart, the nav and
   every reveal behind a WebGL library resolving correctly. */
async function enhance() {
  try {
    const { weaveAll } = await import("./weave.js");
    weaveAll(() => Pressure.value);
  } catch (e) {
    console.warn("acete: weave unavailable, plates stay as photographs", e);
  }
  try {
    const { initShopFilter } = await import("./shopfilter.js");
    initShopFilter();
  } catch (e) {
    console.warn("acete: filter drawer unavailable, the form still works", e);
  }
  try {
    const { initCountdown } = await import("./countdown.js");
    initCountdown();
  } catch (e) {
    console.warn("acete: drop counter unavailable, the date still reads", e);
  }
  try {
    const { initHeroSequence } = await import("./hero-sequence.js");
    initHeroSequence();
  } catch (e) {
    // The entrance is a CSS animation and has already played; what is lost is
    // the advancing of frames. Mark the hero lit anyway so anything keyed to
    // that class is in its settled state rather than mid-arrival.
    document.querySelector(".hero--seq")?.classList.add("is-lit");
    console.warn("acete: hero sequence unavailable, first frame stands", e);
  }
}

/* ----------------------------------------------------------------- boot --- */
function boot() {
  // We are alive, so the layout's failsafe is not needed. Cancelling it rather
  // than racing it is what lets the deadline be short: it now fires only when
  // this file genuinely never ran.
  clearTimeout(window.__aceteGuard);

  initScroll();
  initReveals();
  initNav();
  initNavMenus();
  initShopScroll();
  initAccordions();
  initDrawer();
  initChoreography();
  initPlateIndex();
  Cart.refresh().catch(() => {});
  enhance();
  root.classList.add("is-ready");
}

if (document.readyState === "loading")
  document.addEventListener("DOMContentLoaded", boot);
else boot();

/* The theme editor swaps section markup in without a reload, so a re-rendered
   section needs its reveals, its accordions and its weave back. `weaveAll` is
   only ever in scope inside enhance(), so calling it here threw a ReferenceError
   and took the rest of the handler's work with it — in the editor, which is
   exactly where the merchant is looking. Import it again, guarded, like every
   other use of that module. */
document.addEventListener("shopify:section:load", (e) => {
  initReveals(e.target);
  initAccordions(e.target);
  import("./weave.js")
    .then(({ weaveAll }) => weaveAll(() => Pressure.value))
    .catch(() => {});
});

export { REDUCED, initReveals };

/* --------------------------------------------------------- the handoff ---
   product-form.js needs the cart and the money formatter, and it used to get
   them with `import { Cart, money } from "acete"` through the import map. That
   is one bare specifier too many: asset_url hands back a PROTOCOL-RELATIVE url
   (//shop.myshopify.com/cdn/...), so on any page served over http — which is
   every page `shopify theme dev` serves — it resolves to http, cross-origin,
   and the fetch fails. The importing module then never executes at all, which
   is how the entire product form went dead while the file itself loaded 200.

   A global is unfashionable and correct here: same instance, same cart, no
   resolution to get wrong. window.acete already exists; it carries the routes. */
window.acete = Object.assign(window.acete || {}, { Cart, money, Pressure });
